/**
 * Data conversion utilities for eSIM
 * Converts Sanity CMS data to frontend format
 */

import { buildAffiliateLink } from '@/lib/affiliates/builders';

/**
 * Parse validity string to days
 * @param {string} validity - Validity string like "30 days", "7 days", etc.
 * @returns {number} Number of days
 */
function parseValidityToDays(validity) {
    if (!validity) return 30;
    const match = validity.match(/(\d+)/);
    return match ? parseInt(match[1]) : 30;
}

/**
 * Convert Sanity plan to tariff format for frontend
 * @param {Object} plan - Sanity plan object
 * @param {Object} provider - Sanity provider object
 * @param {string} region - Region code ('montenegro', 'europe', etc.)
 * @param {string} countryName - Country name for display
 * @returns {Object} Converted tariff object
 */
export function convertSanityPlanToTariff(plan, provider, region, countryName = null) {
    const regions = region === 'europe' ? ['Europe'] : [countryName || region || 'Local'];

    const planIdentifier =
        plan?._id ||
        plan?._key ||
        plan?.id ||
        (plan?.slug && (plan.slug.current || plan.slug)) ||
        null;

    const fallbackIdentifier = `${provider.code}-${region}-${plan.dataAmount || 'unlimited'}-${plan.validity}-${plan.priceAmount || 0}-${plan.features?.length || 0}`;
    const tariffId = planIdentifier || fallbackIdentifier;

    // Parse provider languages
    const providerLanguages = provider.languages
        ? provider.languages.split(',').map(lang => lang.trim().toLowerCase())
        : [];

    // Build affiliate link
    const baseUrl = plan.deepLink || provider.trackingBaseUrl;
    const affiliateLink = buildAffiliateLink({ provider, baseUrl });

    return {
        id: tariffId,
        provider: provider.title,
        providerId: provider.code,
        providerLogo: provider.logoUrl,
        link: affiliateLink,
        duration: parseValidityToDays(plan.validity),
        data: plan.isUnlimited ? 0 : (plan.dataAmount || 0),
        unlimited: plan.isUnlimited || false,
        calls: false, // Not supported in current Sanity schema
        regions: regions,
        price: plan.priceAmount || 0,
        pricePerGb: plan.dataAmount ? (plan.priceAmount / plan.dataAmount).toFixed(2) : 0,
        pricePerDay: plan.isUnlimited ? (plan.priceAmount / parseValidityToDays(plan.validity)).toFixed(2) : null,
        languages: providerLanguages,
        // Additional fields for compatibility
        validity: plan.validity,
        dataAmount: plan.dataAmount,
        dataUnit: plan.dataUnit,
        priceAmount: plan.priceAmount,
        priceCurrency: plan.priceCurrency,
        coverage: plan.coverage,
        isPayAsYouGo: plan.isPayAsYouGo || false,
        esimPrice: plan.esimPrice,
        mbPrice: plan.mbPrice,
        payAsYouGoCurrency: plan.payAsYouGoCurrency
    };
}

/**
 * Convert multiple Sanity offerings to tariff format
 * @param {Array} offerings - Array of Sanity offerings
 * @param {string} countryName - Country name for display
 * @returns {Array} Array of converted tariffs
 */
export function convertSanityOfferingsToTariffs(offerings, countryName = null) {
    const convertedTariffs = [];

    offerings.forEach(offering => {
        if (offering.plans) {
            offering.plans.forEach(plan => {
                const region = offering.country?.slug?.current || 'montenegro';
                convertedTariffs.push(
                    convertSanityPlanToTariff(plan, offering.provider, region, countryName)
                );
            });
        }
    });

    return convertedTariffs;
}

/**
 * Group tariffs by provider
 * @param {Array} tariffs - Array of tariff objects
 * @returns {Object} Object with provider codes as keys
 */
export function groupTariffsByProvider(tariffs) {
    return tariffs.reduce((acc, tariff) => {
        const providerId = tariff.providerId;
        if (!acc[providerId]) {
            acc[providerId] = [];
        }
        acc[providerId].push(tariff);
        return acc;
    }, {});
}

/**
 * Filter tariffs by coverage type
 * @param {Array} tariffs - Array of tariff objects
 * @param {string} coverage - Coverage type ('local', 'regional', 'global')
 * @returns {Array} Filtered tariffs
 */
export function filterTariffsByCoverage(tariffs, coverage) {
    return tariffs.filter(tariff => {
        if (coverage === 'local') {
            return tariff.coverage === 'local' ||
                (tariff.regions && tariff.regions.length === 1 && !tariff.regions.includes('Europe'));
        }
        if (coverage === 'regional') {
            return tariff.coverage === 'regional' ||
                (tariff.regions && tariff.regions.includes('Europe'));
        }
        if (coverage === 'global') {
            return tariff.coverage === 'global';
        }
        return true;
    });
}

/**
 * Calculate relevance score for tariff based on user preferences
 * @param {Object} tariff - Tariff object
 * @param {Object} preferences - User preferences
 * @returns {number} Relevance score (higher is better)
 */
export function calculateTariffRelevance(tariff, preferences) {
    let score = 0;

    // Region preference
    if (preferences.region === 'local' && tariff.coverage === 'local') {
        score += 10;
    } else if (preferences.region === 'regional' && tariff.coverage === 'regional') {
        score += 10;
    }

    // Duration preference
    if (preferences.duration && tariff.duration) {
        const durationMatch = Math.abs(tariff.duration - preferences.duration) <= 7;
        if (durationMatch) score += 8;
    }

    // Data preference
    if (preferences.dataAmount && tariff.dataAmount) {
        const dataMatch = Math.abs(tariff.dataAmount - preferences.dataAmount) <= 5;
        if (dataMatch) score += 8;
    }

    // Language preference
    if (preferences.language && tariff.languages) {
        if (tariff.languages.includes(preferences.language)) {
            score += 5;
        }
    }

    return score;
}
